import React, { useEffect } from "react";
import {
  CalendarOutlined,
  ClusterOutlined,
  ControlOutlined,
  LockOutlined,
  UserOutlined,
  TeamOutlined,
  DatabaseOutlined,
  HomeOutlined,
  DeleteOutlined,
  ApartmentOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { ConfigProvider } from 'antd';
import { useTranslation } from 'react-i18next';
import { getAntdLocale } from 'util/antdLocale';
import { BRAND, BRAND_THEME, DARK } from 'theme/tokens';
import antdTheme from 'antd/es/theme';
import SideMenu from 'components/General/sideMenu';
import styles from "./style.module.scss";
import i18n from 'i18next.js';

const THEME_TOKENS = {
  dark: {
    algorithm: antdTheme.darkAlgorithm,
    colorPrimary: BRAND.primary,
    colorBgBase: DARK.bgBase,
    colorBgContainer: DARK.bgSurface,
    colorBgElevated: DARK.bgElevated,
    colorText: '#ffffff',
    colorBorder: DARK.bgSubtle,
  },
  light: {
    algorithm: antdTheme.defaultAlgorithm,
    colorPrimary: BRAND.primary,
    colorBgBase: '#ffffff',
    colorBgContainer: '#E8E0F4',
    colorBgElevated: '#ffffff',
    colorText: '#000000',
    colorBorder: '#E8E0F4',
  },
  brand: {
    algorithm: antdTheme.defaultAlgorithm,
    colorPrimary: BRAND_THEME.chrome,
    colorBgBase: '#ffffff',
    colorBgContainer: '#f4f4f4',
    colorBgElevated: '#ffffff',
    colorText: '#4A4D4E',
    colorBorder: '#f4f4f4',
  },
};

const buildAntdTheme = (themeName) => {
  const t = THEME_TOKENS[themeName] || THEME_TOKENS.dark;
  return {
    algorithm: t.algorithm,
    token: {
      colorPrimary: t.colorPrimary,
      colorBgBase: t.colorBgBase,
      colorBgLayout: t.colorBgBase,
      colorBgContainer: t.colorBgContainer,
      colorBgElevated: t.colorBgElevated,
      colorText: t.colorText,
      colorBorder: t.colorBorder,
    },
  };
};

const sideMenuOptions = (isAdmin, canManageTeams, plan) => {
  const isPlan68 = plan === 68;
  const showTeamManagement = isAdmin || canManageTeams;
  return [
    {
      name: i18n.t("nav.ia_my_ai_insights", "My AI Insights"),
      icon: <HomeOutlined />,
      key: "home",
    },
    {
      name: i18n.t("settings_sidebar.1"),
      icon: <UserOutlined />,
      key: "details",
    },
    {
      name: i18n.t("settings_sidebar.2"),
      icon: <LockOutlined />,
      key: "security",
    },
    // Preferences & Integrations: hide only for plan 68 (restricted plan)
    ...(!isPlan68 ? [
      {
        name: i18n.t("settings_sidebar.3"),
        icon: <ControlOutlined />,
        key: "preferences",
      },
      {
        name: i18n.t("settings_sidebar.4"),
        icon: <ClusterOutlined />,
        key: "integrations",
      },
    ] : []),
    // My Plan: visible to everyone
    {
      name: i18n.t("settings_sidebar.5"),
      icon: <CalendarOutlined />,
      key: "plan",
    },
    {
      name: i18n.t("settings_sidebar.11"),
      icon: <ThunderboltOutlined />,
      key: "ai-credits",
    },
    // Teams & Users: power user or delegated org admin
    ...(showTeamManagement ? [
      {
        name: i18n.t("settings_sidebar.6"),
        icon: <DatabaseOutlined />,
        key: "teams",
      },
      {
        name: i18n.t("settings_sidebar.7"),
        icon: <TeamOutlined />,
        key: "users",
      },
    ] : []),
    {
      name: i18n.t("settings_sidebar.9", "Hot Term Blacklist"),
      icon: <DeleteOutlined />,
      key: "hot-terms",
    },
    {
      name: i18n.t("settings_sidebar.10", "Topic Library"),
      icon: <ApartmentOutlined />,
      key: "topic-library",
    },
  ];
};

const Representation = ({ children, onChange, selected, isAdmin, canManageTeams, isEnterprisePowerUser, plan, subscriptionRow = null, user = null, ...props }) => {
  const themeName = props.theme || 'dark';
  const { i18n } = useTranslation();
  const antdLocale = getAntdLocale(i18n.language);

  return (
    <ConfigProvider theme={buildAntdTheme(themeName)} locale={antdLocale}>
      <div className={styles.container}>
        <SideMenu
          selected={selected}
          onChange={onChange}
          isAdmin={isAdmin}
          canManageTeams={canManageTeams}
          isEnterprisePowerUser={isEnterprisePowerUser}
          plan={plan}
          user={user}
          subscriptionRow={subscriptionRow}
          theme={themeName}
        />
        <div className={styles.content}>
          {children}
        </div>
      </div>
    </ConfigProvider>
  )
}

export default Representation;